import React, { useEffect, useRef, useState, PropsWithChildren } from "react";
import { Theme } from "@emotion/react";
import { borderRadius, styled } from "@iventis/styles";

export type TooltipOnElementHoverProps = {
    cursorX: number;
    cursorY: number;
    containerElementLeft: number;
    containerElementRight: number;
    containerElementTop: number;
    testId?: string;
};

export function HoverTooltip({
    cursorX,
    cursorY,
    containerElementLeft,
    containerElementRight,
    containerElementTop,
    testId,
    children,
}: PropsWithChildren<TooltipOnElementHoverProps>) {
    const ref = useRef<HTMLDivElement>(null);

    const [transform, setTransform] = useState<string>();

    useEffect(() => {
        const width = ref.current.offsetWidth;
        const height = ref.current.offsetHeight;
        const gapFromEdge = 10;

        let xTranslation: string;

        // X may need to be adjusted to keep the tooltip in the screen

        if (cursorX < containerElementLeft + gapFromEdge + width / 2) {
            // Nudge inward from the left edge; don't apply a translate to the left
            xTranslation = `translateX(${containerElementLeft}px) translateX(${gapFromEdge}px)`;
        }

        if (cursorX > containerElementRight - width / 2) {
            // Nudge inward from the right edge; apply a full width translate to the left
            xTranslation = `translateX(${containerElementRight}px) translateX(-100%) translateX(${-gapFromEdge}px)`;
        }

        if (xTranslation === undefined) {
            // No nudging required; apply a 50% translate so the popup is centered on the X axis relative to the cursor
            xTranslation = `translateX(${cursorX}px) translateX(-50%)`;
        }

        let yTranslation: string;

        // Y may need to be adjusted to keep the tooltip in the screen
        if (cursorY < containerElementTop + height) {
            yTranslation = `translateY(${cursorY}px) translateY(30px)`;
        } else {
            yTranslation = `translateY(${cursorY}px) translateY(-20px) translateY(-100%)`;
        }
        setTransform(`${xTranslation} ${yTranslation}`);
    }, [cursorX, cursorY]);

    return (
        <StyledContainer ref={ref} transform={transform} data-testid={testId}>
            {children}
        </StyledContainer>
    );
}

const StyledContainer = styled.div<{ transform: string }>`
    border-radius: ${borderRadius.standard};
    background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued50};
    color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
    visibility: ${({ transform }) => (transform ? "visible" : "hidden")};
    position: absolute;
    top: 0;
    left: 0;
    padding: 10px;
    min-width: 100px;
    max-width: 200px;
    transform: ${({ transform }) => transform};
    pointer-events: "none";
    transition: "opacity 100ms ease-in-out";
`;
